import { SearchFilterViews } from 'js/constants/visibilityFilters';
import {
  difference,
  filter,
  find,
  forEach,
  has,
  indexOf,
  intersection,
  isEmpty,
  map,
  mapValues,
  max,
  orderBy,
  reduce,
  reverse,
  slice,
} from 'lodash';
import {
  CLONE_FLOW_SUCCESS,
  CREATE_RAG_FLOW_SUCCESS,
  CREATE_API_SERVER_FLOW_SUCCESS,
  DELETE_DATASET_SUCCESS,
  DELETE_PIPELINE_SUCCESS,
  DELETE_SINK_SUCCESS,
  DELETE_SOURCE_SUCCESS,
  DESELECT_ALL_PIPELINE_ITEMS,
  GET_DASHBOARD_METRICS_SUCCESS,
  GET_FLOW_RUNS_SUCCESS,
  GET_PIPELINES_REQUEST,
  GET_PIPELINES_SUCCESS,
  GET_PIPELINE_DOC_SUCCESS,
  LOGOUT_COMPLETE,
  REFRESH_PIPELINE_REQUEST,
  REFRESH_PIPELINE_SUCCESS,
  REMOVE_DETAIL_VIEW_CARD,
  SEARCH_PIPELINES_SUCCESS,
  SELECT_PIPELINE_ITEM,
  SET_DETAIL_VIEW_CARD,
  SET_PIPELINE_ACTION,
  UPDATE_PIPELINE_DOC_SUCCESS,
  GET_FLOWS_WITHOUT_PROJECTS_SUCCESS,
  GET_FLOWS_SQL_AUTOMATIONS_SUCCESS,
  GET_MCP_FLOWS_SUCCESS,
} from 'js/constants/actionTypes';
import { getResourceEntity } from 'js/lib/PipelineUtils';

const initialState = {
  selected: null,
  edges: [],
  parentRelationships: {},
  branches: {},
  rootNodeKeys: [],
  searchNodeKeys: [],
  treeHeights: {},
  longestDataSetChainLengths: {},
  flat: [],
  isPreview: false,
  pipelineAction: null,
  lastUpdated: null,
  refreshing: {},
  detailView: {
    id: null,
    type: null,
    nodeKey: null,
    preSelectedTab: null,
  },
  docById: {},
};

export const pipelineReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_PIPELINES_REQUEST:
      return setIsPreview(state, action);

    case GET_PIPELINES_SUCCESS:
    case GET_DASHBOARD_METRICS_SUCCESS:
    case CREATE_RAG_FLOW_SUCCESS:
    case CREATE_API_SERVER_FLOW_SUCCESS:
    case GET_FLOWS_WITHOUT_PROJECTS_SUCCESS:
    case GET_FLOWS_SQL_AUTOMATIONS_SUCCESS:
    case GET_MCP_FLOWS_SUCCESS:
    case GET_FLOW_RUNS_SUCCESS:
      return savePipelines(state, action);

    case CLONE_FLOW_SUCCESS:
    case SEARCH_PIPELINES_SUCCESS:
    case REFRESH_PIPELINE_SUCCESS:
      return refreshPipeline(state, action);

    case REFRESH_PIPELINE_REQUEST:
      return saveRefreshingPipeline(state, action);

    case SELECT_PIPELINE_ITEM:
      return selectItem(state, action);

    case DESELECT_ALL_PIPELINE_ITEMS:
      return deselectAllItems(state, action);

    case DELETE_PIPELINE_SUCCESS:
    case DELETE_DATASET_SUCCESS:
    case DELETE_SINK_SUCCESS:
    case DELETE_SOURCE_SUCCESS:
      return deleteResource(state, action);

    case SET_PIPELINE_ACTION:
      return setPipelineAction(state, action);

    case SET_DETAIL_VIEW_CARD:
      return setDetailViewCard(state, action);

    case REMOVE_DETAIL_VIEW_CARD:
      return removeDetailViewCard(state);

    case GET_PIPELINE_DOC_SUCCESS:
    case UPDATE_PIPELINE_DOC_SUCCESS:
      return savePipelineDoc(state, action);

    case LOGOUT_COMPLETE:
      return initialState;

    default:
      return state;
  }
};

export default pipelineReducer;

function setIsPreview(state, action) {
  return {
    ...state,
    isPreview: (action.requestInfo || {}).isPreview,
  };
}

function getOrderedFlowIds(result) {
  if (!result?.flows) return [];
  const raw = result.flows;
  return Array.isArray(raw) ? raw : (raw.id ?? []);
}

function savePipelines(state, action) {
  const id = getOrderedFlowIds(action.result);
  const { entities, requestInfo, receivedAt } = action;

  const { view } = requestInfo || {};

  // edges
  const newState = calculateEdges(entities, state);

  /**
   * Prevent rootNodeKeys and searchNodeKeys state from being overwritten on GET_FLOW_RUNS_SUCCESS,
   * These keys are not relevant for flow runs responses, because Flow Runs page handle its own node keys.
   */
  if (GET_FLOW_RUNS_SUCCESS === action.type) {
    return {
      ...state,
      ...newState,
      isPreview: false,
      lastUpdated: receivedAt,
    };
  }

  if ([GET_FLOWS_SQL_AUTOMATIONS_SUCCESS, GET_MCP_FLOWS_SUCCESS].includes(action.type)) {
    return {
      ...state,
      ...newState,
      isPreview: false,
      lastUpdated: receivedAt,
    };
  }

  return {
    ...state,
    ...newState,
    ...(view !== SearchFilterViews.SEARCH_PIPELINES
      ? { rootNodeKeys: id }
      : { searchNodeKeys: id }),
    isPreview: false,
    lastUpdated: receivedAt,
  };
}

function calculateEdges(entities, state) {
  const { flowNodes } = entities;

  let edges = [];
  let parentRelationships = {};
  forEach(flowNodes, (node) => {
    if (has(node, 'children') && node.children.length) {
      forEach(node.children, (to) => {
        let thisEdge = {
          from: node.nodeKey,
          to,
        };
        if (has(flowNodes, to)) {
          let toItem = flowNodes[to];
          thisEdge.selected = has(toItem, 'selected') ? toItem.selected : false; // is this a selected edge
          thisEdge.external = has(toItem, 'external') ? toItem.external : false; //is this an outside org line
          thisEdge.transform = has(toItem, 'transform') ? toItem.transform : false; //is this a transformed Nexset
        } else {
          thisEdge.selected = false; // is this a selected edge
          thisEdge.external = false; //is this an outside org line
          thisEdge.transform = false; //is this a transformed Nexset
        }
        edges.push(thisEdge);
        parentRelationships[to] = node.nodeKey;
      });
    }
  });

  // calculate branches
  const branches = {}; // item ID to branch map where branch is { items, edges }
  forEach(flowNodes, (item) => {
    const ancestors = getAncestors(item, flowNodes, parentRelationships);
    const descendants = getDescendants(item, flowNodes);
    const branchItems = reverse(ancestors).concat([item.nodeKey]).concat(descendants);
    const branchEdges = [];
    forEach(edges, (edge, edgeIndex) => {
      // if both ends of this edge have a branch item on them, include this edge
      if (
        branchEdges.indexOf(edgeIndex) < 0 &&
        branchItems.indexOf(edge.from) > -1 &&
        branchItems.indexOf(edge.to) > -1
      ) {
        branchEdges.push(edgeIndex);
      }
    });
    branches[item.nodeKey] = {
      items: branchItems,
      edges: branchEdges,
    };
  });

  // find tree heights
  const longestDataSetChainLengths = {};
  const treeHeights = {};
  let flat = [];

  function getSourceAndDerivedDataSets(branchDataSets) {
    const sortedDataSets = orderBy(
      map(branchDataSets, (nodeKey) => flowNodes[nodeKey]),
      'depth',
    );
    const dataSets = [];

    forEach(sortedDataSets, (item, index) => {
      if (index > 0 && sortedDataSets[index - 1].depth + 1 !== item.depth) {
        return false;
      }

      dataSets.push(item.nodeKey);
    });

    return dataSets;
  }

  forEach(flowNodes, (item) => {
    if (isEmpty(item.children)) {
      const leafNode = item.nodeKey;
      const branchItems = branches[leafNode] ? branches[leafNode].items : [leafNode];
      const rootNode =
        find(branchItems, (branchItem) => !has(parentRelationships, branchItem)) || leafNode;
      let branchDataSets = [];

      if (flowNodes[rootNode].type === 'SOURCE') {
        branchDataSets = getSourceAndDerivedDataSets(
          filter(branchItems, (nodeKey) => flowNodes[nodeKey].type === 'DATA_SET'),
        );
        flat.push({
          root: rootNode,
          leaf: leafNode !== rootNode ? leafNode : null,
          dataSets: branchDataSets,
        });
      } else if (
        flowNodes[rootNode].type === 'DATA_SET' &&
        !isEmpty(flowNodes[rootNode].children)
      ) {
        branchDataSets = getSourceAndDerivedDataSets(
          filter(
            branchItems,
            (nodeKey) => flowNodes[nodeKey].type === 'DATA_SET' && nodeKey !== rootNode,
          ),
        );
        flat.push({
          root: rootNode,
          leaf: leafNode !== rootNode ? leafNode : null,
          dataSets: branchDataSets,
        });
      }

      // measure the length of the longest continuous Nexset chain
      const longestDataSetChainLength = max([
        longestDataSetChainLengths[rootNode],
        branchDataSets.length,
      ]);
      longestDataSetChainLengths[rootNode] = longestDataSetChainLength;

      treeHeights[rootNode] = 1;
    }
  });

  flat = orderBy(
    flat,
    [
      ({ root }) => root && getResourceEntity(flowNodes[root], entities, false)?.created_at,
      ({ leaf }) => leaf && getResourceEntity(flowNodes[leaf], entities, false)?.created_at,
    ],
    ['desc', 'desc'],
  );

  forEach(flat, ({ root, leaf, dataSets }) => {
    // const treeHeight = leaf ? branches[leaf].items.length : 1;
    const treeHeight = leaf
      ? branches[leaf].items.length - dataSets.length + longestDataSetChainLengths[root]
      : 1;
    treeHeights[root] = max([treeHeights[root] || 0, treeHeight]);
  });

  return {
    edges,
    parentRelationships,
    // we need to keep old branches to prevent flows not getting rendered
    branches: {
      ...state.branches,
      ...branches,
    },
    treeHeights,
    longestDataSetChainLengths,
    flat,
  };
}

function getAncestors(item, flowNodes, parentRelationships) {
  const ancestors = [];
  const parentKey = parentRelationships[item.nodeKey];

  if (parentKey) {
    const parent = flowNodes[parentKey];
    ancestors.push(parentKey);
    ancestors.push.apply(ancestors, getAncestors(parent, flowNodes, parentRelationships));
  }

  return ancestors;
}

function getDescendants(item, flowNodes) {
  const descendants = [];

  if (item.children) {
    descendants.push.apply(descendants, item.children);
    forEach(item.children, (childId) =>
      descendants.push.apply(descendants, getDescendants(flowNodes[childId], flowNodes)),
    );
  }

  return descendants;
}

function saveRefreshingPipeline(state, action) {
  const { requestInfo = {} } = action;
  const { nodeKey } = requestInfo;
  return {
    ...state,
    refreshing: {
      ...state.refreshing,
      [nodeKey]: true,
    },
  };
}

function refreshPipeline(state, action) {
  const { receivedAt, requestInfo = {} } = action;
  const { addToRoot = true, nodeKey } = requestInfo || {};
  const { rootNodeKeys } = state;
  const loadedPipeline = savePipelines(state, action);
  const { edges, parentRelationships, branches, treeHeights, longestDataSetChainLengths } =
    deleteResource(state, action);
  loadedPipeline.branches = mapValues(loadedPipeline.branches, (branch) => ({
    ...branch,
    edges: map(branch.edges, (edgeIndex) => edgeIndex + edges.length),
  }));

  // when a pipeline is cloned, prepend its root node key to rootNodeKeys
  let newRootNodeKeys = rootNodeKeys;

  if (isEmpty(intersection(loadedPipeline.rootNodeKeys, rootNodeKeys))) {
    if (addToRoot) {
      newRootNodeKeys = [...loadedPipeline.rootNodeKeys, ...rootNodeKeys];
    }
  }

  return {
    ...state,
    rootNodeKeys: newRootNodeKeys,
    edges: [...edges, ...loadedPipeline.edges],
    parentRelationships: {
      ...parentRelationships,
      ...loadedPipeline.parentRelationships,
    },
    branches: {
      ...branches,
      ...loadedPipeline.branches,
    },
    treeHeights: {
      ...treeHeights,
      ...loadedPipeline.treeHeights,
    },
    longestDataSetChainLengths: {
      ...longestDataSetChainLengths,
      ...loadedPipeline.longestDataSetChainLengths,
    },
    refreshing: {
      ...state.refreshing,
      ...(nodeKey ? { [nodeKey]: false } : {}),
    },
    lastUpdated: receivedAt,
  };
}

function selectItem(state, action) {
  const { branches } = state;
  const { nodeKey } = action;
  const { edges } = deselectAllItems(state);
  const { edges: branchEdges } = branches[nodeKey];

  // select branch edges
  forEach(branchEdges, (branchEdgeIndex) => {
    edges[branchEdgeIndex].selected = true;
  });

  return {
    ...state,
    selected: nodeKey,
    edges,
  };
}

function deselectAllItems(state) {
  const { edges } = state;
  const newEdges = [...edges];

  forEach(newEdges, (edge) => {
    edge.selected = false;
  });

  return {
    ...state,
    edges: newEdges,
    selected: null,
  };
}

function deleteResource(state, action) {
  const {
    requestInfo: { nodeKey },
  } = action;
  const { selected, edges, parentRelationships, branches, rootNodeKeys } = state;
  const branchItems = branches[nodeKey]?.items;
  const deletedItems = slice(branchItems, indexOf(branchItems, nodeKey));
  const newSelected = selected && indexOf(deletedItems, selected) !== -1 ? null : selected;
  const newRootNodeKeys = deleteRootNodeKeys(rootNodeKeys, deletedItems);
  const newParentRelationships = deleteParentRelationships(parentRelationships, deletedItems);
  const { newEdges, removedEdgeIds, changedEdgeIds } = deleteEdges(edges, deletedItems);
  const newBranches = deleteBranches(branches, deletedItems, removedEdgeIds, changedEdgeIds);

  return {
    ...state,
    selected: newSelected,
    rootNodeKeys: newRootNodeKeys,
    parentRelationships: newParentRelationships,
    edges: newEdges,
    branches: newBranches,
  };
}

function deleteRootNodeKeys(nodeKeys, idsToRemove) {
  return difference(nodeKeys, idsToRemove);
}

function deleteParentRelationships(relationships, idsToRemove) {
  return reduce(
    relationships,
    (acc, currRel, currKey) => {
      if (indexOf(idsToRemove, currRel) !== -1 || indexOf(idsToRemove, currKey) !== -1) {
        return acc;
      }
      acc[currKey] = currRel;
      return acc;
    },
    {},
  );
}

function deleteBranches(branches, itemsToRemove, edgesToRemove, newEdgeIds) {
  return reduce(
    branches,
    (acc, currBranch, currKey) => {
      if (indexOf(itemsToRemove, currKey) !== -1) {
        return acc;
      }
      let { items, edges } = currBranch;
      let newItems = difference(items, itemsToRemove);
      let newEdges = difference(edges, edgesToRemove);
      newEdges = map(newEdges, (edge) => newEdgeIds[edge]);

      acc[currKey] = {
        items: newItems,
        edges: newEdges,
      };
      return acc;
    },
    {},
  );
}

function deleteEdges(edges, idsToRemove) {
  let removedEdgeIds = [];
  let changedEdgeIds = {};
  let currentId = 0;
  let newEdges = reduce(
    edges,
    (acc, edge, idx) => {
      let { from, to } = edge;
      if (indexOf(idsToRemove, from) !== -1 || indexOf(idsToRemove, to) !== -1) {
        //remove this edge.
        removedEdgeIds.push(idx);
        // acc.push(edge);
      } else {
        changedEdgeIds[idx] = currentId;
        acc.push(edge);
        currentId++;
      }
      return acc;
    },
    [],
  );

  return { newEdges, removedEdgeIds, changedEdgeIds };
}

function setPipelineAction(state, action) {
  const { pipelineAction } = action;

  return {
    ...state,
    pipelineAction,
  };
}

function setDetailViewCard(state, action) {
  return {
    ...state,
    detailView: {
      id: action.selectedCard.id,
      type: action.selectedCard.type,
      nodeKey: action.selectedCard.nodeKey,
      preSelectedTab: action.preSelectedTab,
    },
  };
}

function removeDetailViewCard(state) {
  return {
    ...state,
    detailView: { id: null, type: null, nodeKey: null, preSelectedTab: null },
  };
}

function savePipelineDoc(state, action) {
  const {
    result,
    requestInfo: { id },
  } = action;
  return {
    ...state,
    docById: {
      [id]: isEmpty(result) ? null : result[0],
    },
  };
}
